import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Users,
  BookOpen,
  Brain,
  Clock,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  FileText,
  Calendar,
  BarChart3,
  Plus,
  TrendingUp,
  Activity,
} from 'lucide-react';

export default function TeacherDashboard() {
  // Mock data
  const classroomStats = {
    totalStudents: 127,
    activeClasses: 5,
    totalCourses: 18,
    completedAssignments: 89,
  };

  const recentActivity = [
    { id: 1, type: 'submission', student: 'Alex Johnson', course: 'Math 101', time: '2 hours ago', status: 'pending' },
    {
      id: 2,
      type: 'completion',
      student: 'Sarah Wilson',
      course: 'Science Lab',
      time: '4 hours ago',
      status: 'completed',
    },
    { id: 3, type: 'question', student: 'Mike Chen', course: 'History', time: '1 day ago', status: 'answered' },
    { id: 4, type: 'submission', student: 'Emma Davis', course: 'English', time: '1 day ago', status: 'graded' },
  ];

  const classrooms = [
    {
      id: 1,
      name: 'Grade 10A - Mathematics',
      students: 28,
      courses: 4,
      progress: 78,
      lastActivity: '2 hours ago',
      botStatus: 'active',
    },
    {
      id: 2,
      name: 'Grade 9B - Science',
      students: 25,
      courses: 3,
      progress: 65,
      lastActivity: '5 hours ago',
      botStatus: 'training',
    },
    {
      id: 3,
      name: 'Grade 11C - English',
      students: 30,
      courses: 5,
      progress: 82,
      lastActivity: '1 day ago',
      botStatus: 'active',
    },
  ];

  const upcomingDeadlines = [
    { course: 'Math 101', assignment: 'Algebra Quiz', dueDate: 'Today, 3:00 PM', submissions: 18, total: 28 },
    { course: 'Science Lab', assignment: 'Lab Report #3', dueDate: 'Tomorrow, 11:59 PM', submissions: 22, total: 25 },
    { course: 'History', assignment: 'Essay on WWI', dueDate: 'Mar 25, 11:59 PM', submissions: 15, total: 30 },
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-amber-500" />;
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'answered':
        return <MessageSquare className="w-4 h-4 text-primary" />;
      case 'graded':
        return <FileText className="w-4 h-4 text-violet-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getBotStatusBadge = (status) => {
    const statusConfig = {
      active: {
        color: 'bg-emerald-500/10 text-emerald-700 border-emerald-200 dark:text-emerald-400 dark:border-emerald-800',
        text: 'Active',
      },
      training: {
        color: 'bg-blue-500/10 text-blue-700 border-blue-200 dark:text-blue-400 dark:border-blue-800',
        text: 'Training',
      },
      inactive: { color: 'bg-muted text-muted-foreground border-border', text: 'Inactive' },
    };
    const config = statusConfig[status] || statusConfig.inactive;
    return <Badge className={config.color}>{config.text}</Badge>;
  };

  return (
    <ScrollArea className="flex-1">
      <div className="overflow-hidden h-full bg-muted/20">
        {/* Main Content */}
        <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Welcome Section */}
          <div className="mb-8 p-6 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-lg border">
            <h2 className="text-3xl font-bold text-foreground">Welcome back, Prof. Anderson</h2>
            <p className="text-muted-foreground mt-2 text-lg">Here's what's happening in your classrooms today</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-950/50 dark:to-blue-900/30 border-blue-200 dark:border-blue-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Students</CardTitle>
                <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-full">
                  <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {classroomStats.totalStudents}
                </div>
                <p className="text-xs text-blue-600 dark:text-blue-400 flex items-center">
                  <TrendingUp className="w-3 h-3 mr-1" />
                  +12 from last month
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-950/50 dark:to-emerald-900/30 border-emerald-200 dark:border-emerald-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-emerald-700 dark:text-emerald-300">
                  Active Classes
                </CardTitle>
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/50 rounded-full">
                  <BookOpen className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">
                  {classroomStats.activeClasses}
                </div>
                <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center">
                  <Activity className="w-3 h-3 mr-1" />
                  All systems running
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-violet-50 to-violet-100/50 dark:from-violet-950/50 dark:to-violet-900/30 border-violet-200 dark:border-violet-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-violet-700 dark:text-violet-300">
                  Total Courses
                </CardTitle>
                <div className="p-2 bg-violet-100 dark:bg-violet-900/50 rounded-full">
                  <FileText className="h-4 w-4 text-violet-600 dark:text-violet-400" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-violet-900 dark:text-violet-100">
                  {classroomStats.totalCourses}
                </div>
                <p className="text-xs text-violet-600 dark:text-violet-400 flex items-center">
                  <Plus className="w-3 h-3 mr-1" />
                  +3 new this week
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-950/50 dark:to-amber-900/30 border-amber-200 dark:border-amber-800">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-amber-700 dark:text-amber-300">AI Bots Active</CardTitle>
                <div className="p-2 bg-amber-100 dark:bg-amber-900/50 rounded-full">
                  <Brain className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-amber-900 dark:text-amber-100">3/5</div>
                <p className="text-xs text-amber-600 dark:text-amber-400">2 in training mode</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* My Classrooms */}
            <Card className="lg:col-span-1 bg-muted/30">
              <CardHeader className="bg-muted/40 rounded-t-lg">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">My Classrooms</CardTitle>
                  <Button size="sm" variant="outline" className="bg-background/80 hover:bg-background">
                    <Plus className="w-4 h-4 mr-2" />
                    New Class
                  </Button>
                </div>
                <CardDescription>Overview of your active classrooms</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {classrooms.map((classroom) => (
                    <div
                      key={classroom.id}
                      className="flex items-center justify-between p-4 bg-background/60 border rounded-lg hover:bg-muted/60 transition-all duration-200 hover:shadow-md">
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground mb-1">{classroom.name}</h4>
                        <div className="flex items-center space-x-4 mt-1 text-sm text-muted-foreground">
                          <span className="flex items-center">
                            <Users className="w-3 h-3 mr-1" />
                            {classroom.students} students
                          </span>
                          <span className="flex items-center">
                            <BookOpen className="w-3 h-3 mr-1" />
                            {classroom.courses} courses
                          </span>
                          <span className="flex items-center">
                            <Activity className="w-3 h-3 mr-1" />
                            {classroom.lastActivity}
                          </span>
                        </div>
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-muted-foreground">Learning Progress</span>
                            <span className="font-medium text-foreground">{classroom.progress}%</span>
                          </div>
                          <Progress value={classroom.progress} className="h-2.5" />
                        </div>
                      </div>
                      <div className="ml-6 flex flex-col items-end space-y-3">
                        {getBotStatusBadge(classroom.botStatus)}
                        <Button variant="ghost" size="sm" className="hover:bg-primary/10">
                          View Details
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="bg-muted/30">
              <CardHeader className="bg-muted/40 rounded-t-lg">
                <CardTitle className="text-lg">Recent Activity</CardTitle>
                <CardDescription>Latest student interactions across all classes</CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  {recentActivity.map((activity) => (
                    <div
                      key={activity.id}
                      className="flex items-center space-x-3 p-4 bg-background/60 border rounded-lg hover:bg-muted/60 transition-all duration-200">
                      <div className="p-2 bg-muted/60 rounded-full">{getStatusIcon(activity.status)}</div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-foreground">{activity.student}</p>
                        <p className="text-sm text-muted-foreground">{activity.course}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-muted-foreground mb-1">{activity.time}</p>
                        <Badge variant="secondary" className="text-xs bg-muted text-muted-foreground">
                          {activity.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Deadlines */}
          <Card className="mt-8 bg-muted/30">
            <CardHeader className="bg-muted/40 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Upcoming Deadlines</CardTitle>
                  <CardDescription>Assignments and quizzes due soon</CardDescription>
                </div>
                <Button variant="outline" size="sm" className="bg-background/80 hover:bg-background">
                  <Calendar className="w-4 h-4 mr-2" />
                  View Calendar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {upcomingDeadlines.map((deadline, index) => (
                  <div
                    key={index}
                    className="p-5 bg-background/60 border rounded-lg hover:bg-muted/60 transition-all duration-200 hover:shadow-md">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-foreground">{deadline.assignment}</h4>
                      <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                        {deadline.course}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mb-4 flex items-center">
                      <Clock className="w-3 h-3 mr-1" />
                      {deadline.dueDate}
                    </p>
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-muted-foreground">
                        Submissions: {deadline.submissions}/{deadline.total}
                      </span>
                      <span className="font-semibold text-primary">
                        {Math.round((deadline.submissions / deadline.total) * 100)}%
                      </span>
                    </div>
                    <Progress value={(deadline.submissions / deadline.total) * 100} className="h-2.5" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card className="mt-8 bg-muted/30">
            <CardHeader className="bg-muted/40 rounded-t-lg">
              <CardTitle className="text-lg">Quick Actions</CardTitle>
              <CardDescription>Common tasks and shortcuts</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Button
                  variant="outline"
                  className="h-24 flex-col space-y-3 bg-background/60 hover:bg-primary/10 hover:border-primary/30 transition-all duration-200">
                  <div className="p-2 bg-primary/10 rounded-full">
                    <Plus className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium">Create Course</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-24 flex-col space-y-3 bg-background/60 hover:bg-blue-500/10 hover:border-blue-500/30 transition-all duration-200">
                  <div className="p-2 bg-blue-500/10 rounded-full">
                    <Brain className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <span className="font-medium">Train Bot</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-24 flex-col space-y-3 bg-background/60 hover:bg-emerald-500/10 hover:border-emerald-500/30 transition-all duration-200">
                  <div className="p-2 bg-emerald-500/10 rounded-full">
                    <FileText className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <span className="font-medium">Add Material</span>
                </Button>
                <Button
                  variant="outline"
                  className="h-24 flex-col space-y-3 bg-background/60 hover:bg-violet-500/10 hover:border-violet-500/30 transition-all duration-200">
                  <div className="p-2 bg-violet-500/10 rounded-full">
                    <BarChart3 className="w-5 h-5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <span className="font-medium">View Analytics</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
    </ScrollArea>
  );
}
